/**
 * Dentis Discount Engine
 *
 * Resolves an active storefront discount code via Shopify's own cart machinery
 * and exposes it as `window.DentisActiveDiscount` plus a `dentis:discount-resolved`
 * DOM event so price snippets and the promo-status block can react.
 *
 * Source of truth: Shopify (cart.discount_applications + line_item.discount_allocations).
 * We never re-implement discount math; we mirror what Shopify computes.
 *
 * Failure mode: silent. On any error, `window.DentisActiveDiscount` is set to
 * a benign null state, the resolved event still fires (with `active: false`),
 * and price rendering falls back to the normal Liquid-rendered markup.
 */
(function () {
  'use strict';

  if (!window.DentisDebug) {
    var debugFlag = false;
    try {
      var qs = new URLSearchParams(window.location.search || '');
      if (qs.get('dentis_debug') === '1' || qs.get('dentis_debug') === 'true') debugFlag = true;
    } catch (e) { /* no-op */ }
    try {
      if (window.localStorage && window.localStorage.getItem('dentis:debug') === '1') debugFlag = true;
    } catch (e) { /* no-op */ }
    var noop = function () {};
    window.DentisDebug = {
      enabled: debugFlag,
      log: debugFlag ? (console.log.bind(console, '%c[DentisDiscount]', 'color:#0e01ab;font-weight:bold;')) : noop,
      group: debugFlag ? (console.groupCollapsed ? console.groupCollapsed.bind(console) : console.log.bind(console)) : noop,
      groupEnd: debugFlag ? (console.groupEnd ? console.groupEnd.bind(console) : noop) : noop,
      warn: debugFlag ? (console.warn.bind(console, '%c[DentisDiscount]', 'color:#f5a524;font-weight:bold;')) : noop,
      error: debugFlag ? (console.error.bind(console, '%c[DentisDiscount]', 'color:#cc0000;font-weight:bold;')) : noop
    };
  }
  var DBG = window.DentisDebug;

  if (window.DentisDiscountEngine) {
    DBG.log('engine already initialized, skipping re-init');
    return;
  }

  var STORAGE_KEY = 'dentis:active-discount';
  var STORAGE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  var COOKIE_NAME = 'discount_code';
  var URL_PARAMS = ['discount', 'discount_code'];
  var EVENT_NAME = 'dentis:discount-resolved';

  function nowMs() {
    return new Date().getTime();
  }

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function readUrlParam() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      for (var i = 0; i < URL_PARAMS.length; i++) {
        var v = params.get(URL_PARAMS[i]);
        if (v) return v;
      }
    } catch (e) { /* no-op */ }
    return null;
  }

  function readCachedResult(code) {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.timestamp || (nowMs() - parsed.timestamp) > STORAGE_TTL_MS) return null;
      if ((parsed.code || '').toLowerCase() !== (code || '').toLowerCase()) return null;
      return parsed.resolved || null;
    } catch (e) {
      return null;
    }
  }

  function writeCachedResult(code, resolved) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        code: code,
        timestamp: nowMs(),
        resolved: resolved
      }));
    } catch (e) { /* quota or disabled - fine */ }
  }

  function clearCache() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* no-op */ }
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }, opts || {})).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function applyDiscountToCart(code) {
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount: code })
    });
  }

  function getCart() {
    return fetchJSON('/cart.js');
  }

  function findProbeVariantId() {
    try {
      var nodes = document.querySelectorAll('[data-dentis-price][data-dentis-variant-id]');
      for (var i = 0; i < nodes.length; i++) {
        var vid = nodes[i].getAttribute('data-dentis-variant-id');
        if (vid && /^\d+$/.test(vid)) return vid;
      }
    } catch (e) { /* no-op */ }
    return null;
  }

  function addProbeVariant(variantId) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1, properties: { _dentis_probe: '1' } })
    });
  }

  function removeProbeVariant(variantId) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 0 })
    }).catch(function () { /* no-op */ });
  }

  function cartHasUnappliedCode(cart, code) {
    var codes = (cart && cart.discount_codes) || [];
    var lc = (code || '').toLowerCase();
    for (var i = 0; i < codes.length; i++) {
      if ((codes[i].code || '').toLowerCase() === lc && codes[i].applicable === false) {
        return true;
      }
    }
    return false;
  }

  function appMatchesCode(app, code) {
    var lc = (code || '').toLowerCase();
    return (app.title || '').toLowerCase() === lc
      || (app.code || '').toLowerCase() === lc;
  }

  function buildEligibilityFromCart(cart, code) {
    var eligible = {};
    var cartLevelApps = (cart && cart.cart_level_discount_applications) || [];
    var legacyApps = (cart && cart.discount_applications) || [];
    var apps = cartLevelApps.concat(legacyApps);

    var matchingApp = null;
    for (var i = 0; i < apps.length; i++) {
      if (appMatchesCode(apps[i] || {}, code)) {
        matchingApp = apps[i];
        break;
      }
    }
    if (!matchingApp && apps.length === 1) {
      matchingApp = apps[0];
    }

    if (!matchingApp) {
      var scanItems = (cart && cart.items) || [];
      for (var si = 0; si < scanItems.length; si++) {
        var scanAllocs = (scanItems[si] && (scanItems[si].line_level_discount_allocations || scanItems[si].discount_allocations)) || [];
        for (var sa = 0; sa < scanAllocs.length; sa++) {
          var lineApp = (scanAllocs[sa] && scanAllocs[sa].discount_application) || null;
          if (lineApp && appMatchesCode(lineApp, code)) {
            matchingApp = lineApp;
            apps = apps.concat([lineApp]);
            break;
          }
        }
        if (matchingApp) break;
      }
    }

    var items = (cart && cart.items) || [];
    for (var j = 0; j < items.length; j++) {
      var item = items[j] || {};
      var saved = 0;

      // Modern shape: line_level_discount_allocations[*].discount_application + amount
      var lineAllocs = item.line_level_discount_allocations || item.discount_allocations || [];
      var savedFromLineAllocs = 0;
      for (var k = 0; k < lineAllocs.length; k++) {
        var alloc = lineAllocs[k] || {};
        var allocApp = alloc.discount_application || {};
        var matches = appMatchesCode(allocApp, code) || (apps.length === 1);
        if (matches) savedFromLineAllocs += Number(alloc.amount || 0);
      }
      saved += savedFromLineAllocs;

      // item.discounts[] mirrors line_level_discount_allocations[] for entitled-target
      // codes; only consult it as a fallback when line allocations contributed nothing
      // for this item, to avoid double-counting the same per-code amount.
      if (savedFromLineAllocs === 0) {
        var lineDiscounts = item.discounts || [];
        for (var d = 0; d < lineDiscounts.length; d++) {
          var disc = lineDiscounts[d] || {};
          if ((disc.title || '').toLowerCase() === (code || '').toLowerCase()) {
            saved += Number(disc.amount || 0);
          }
        }
      }

      // Total-discount fallback: when only one cart-level discount is in play
      // and matches our code, item.total_discount captures the across-allocated
      // amount even when neither line_level_discount_allocations nor discounts
      // resolves into a per-code entry.
      if (saved === 0 && matchingApp && appMatchesCode(matchingApp, code)) {
        var totalDisc = Number(item.total_discount || 0);
        if (totalDisc > 0) saved = totalDisc;
      }

      if (saved > 0) {
        var pid = String(item.product_id);
        var vid = String(item.variant_id);
        if (!eligible[pid]) eligible[pid] = { savings_total: 0, variants: {} };
        eligible[pid].savings_total += saved;
        eligible[pid].variants[vid] = (eligible[pid].variants[vid] || 0) + (saved / Math.max(1, item.quantity));
      }
    }
    return { eligible: eligible, app: matchingApp, allApps: apps };
  }

  function appType(app) {
    if (!app) return 'unknown';
    var v = app.value_type || app.type || '';
    var typeStr = (app.type || '').toLowerCase();
    if (typeStr === 'shipping_discount' || typeStr === 'shipping') return 'shipping';
    if (typeStr === 'automatic') {
      // Automatic discounts may also be percentage/fixed; rely on value_type
    }
    if (v === 'percentage') return 'percentage';
    if (v === 'fixed_amount') return 'fixed';
    if (typeStr === 'discount_code' && (app.target_type === 'line_item' || !app.target_type)) {
      return v === 'percentage' ? 'percentage' : 'fixed';
    }
    return typeStr || 'unknown';
  }

  function buildResolved(code, cart) {
    if (!cart) {
      return { active: false, code: code || null };
    }
    var built = buildEligibilityFromCart(cart, code);
    var app = built.app;
    if (!app) {
      return { active: false, code: code || null };
    }
    DBG.log('matched app', app, 'all apps:', built.allApps);
    var type = appType(app);
    if (type === 'shipping') {
      return {
        active: true,
        code: code || app.title || app.code || null,
        type: 'shipping',
        value: Number(app.value || 0),
        value_type: app.value_type || null,
        eligible: {},
        target_selection: app.target_selection || null,
        raw_app: app,
        cart_token: cart.token || null
      };
    }
    return {
      active: true,
      code: code || app.title || app.code || null,
      type: type,
      value: Number(app.value || 0),
      value_type: app.value_type || null,
      total_allocated: Number(app.total_allocated_amount || 0),
      eligible: built.eligible,
      target_selection: app.target_selection || null,
      target_type: app.target_type || null,
      raw_app: app,
      cart_token: cart.token || null
    };
  }

  function dispatchResolved(resolved) {
    window.DentisActiveDiscount = resolved;
    DBG.log('window.DentisActiveDiscount set:', resolved);
    DBG.log('dispatching event', EVENT_NAME);
    try {
      var ev = new CustomEvent(EVENT_NAME, { detail: resolved });
      document.dispatchEvent(ev);
    } catch (e) {
      DBG.warn('CustomEvent constructor failed, using legacy fallback', e);
      var fallback = document.createEvent('CustomEvent');
      fallback.initCustomEvent(EVENT_NAME, false, false, resolved);
      document.dispatchEvent(fallback);
    }
  }

  function resolve() {
    var urlCode = readUrlParam();
    var cookieCode = readCookie(COOKIE_NAME);
    var code = urlCode || cookieCode;
    DBG.log('resolve() inputs', { urlCode: urlCode, cookieCode: cookieCode, picked: code });
    try { console.debug('[DentisDiscountEngine] init', { code: code, cached: !!readCachedResult(code || '') }); } catch (e) { /* no-op */ }

    if (!code) {
      DBG.log('no code in url or cookie, dispatching inactive');
      dispatchResolved({ active: false, code: null });
      return;
    }
    code = String(code).trim();
    if (!code) {
      DBG.log('code resolved to empty string, dispatching inactive');
      dispatchResolved({ active: false, code: null });
      return;
    }

    var cached = readCachedResult(code);
    if (cached) {
      DBG.log('cache HIT for code', code, cached);
      dispatchResolved(cached);
      return;
    }
    DBG.log('cache MISS for code', code, '- POST /cart/update.js');

    applyDiscountToCart(code)
      .then(function (resp) {
        DBG.log('POST /cart/update.js response', resp);
        return resp;
      })
      .catch(function (err) {
        DBG.warn('POST /cart/update.js failed (non-fatal, code may still be saved on cart)', err);
      })
      .then(function () {
        DBG.log('GET /cart.js');
        return getCart();
      })
      .then(function (cart) {
        DBG.log('GET /cart.js response', cart);
        var resolved = buildResolved(code, cart);
        DBG.log('buildResolved output (initial)', resolved);

        if (!resolved.active && cart && cart.item_count === 0 && cartHasUnappliedCode(cart, code)) {
          var probeVid = findProbeVariantId();
          DBG.log('cart empty + code on cart but unapplied; attempting probe', { probeVid: probeVid });
          if (probeVid) {
            return addProbeVariant(probeVid)
              .then(function (probeResp) {
                DBG.log('probe add response', probeResp);
                return getCart();
              })
              .then(function (cart2) {
                DBG.log('GET /cart.js (after probe) response', cart2);
                var resolved2 = buildResolved(code, cart2);
                DBG.log('buildResolved output (after probe)', resolved2);
                return removeProbeVariant(probeVid).then(function () {
                  DBG.log('probe removed');
                  return resolved2;
                });
              })
              .catch(function (err) {
                DBG.warn('probe failed; keeping initial resolved', err);
                return resolved;
              });
          }
        }
        return resolved;
      })
      .then(function (resolved) {
        if (resolved && resolved.active) {
          writeCachedResult(code, resolved);
          DBG.log('cached resolved discount in localStorage');
        } else {
          clearCache();
          DBG.log('no active discount; cleared cache');
        }
        dispatchResolved(resolved || { active: false, code: code });
      })
      .catch(function (err) {
        DBG.error('resolve() failed', err);
        clearCache();
        dispatchResolved({ active: false, code: code });
      });
  }

  window.DentisDiscountEngine = {
    resolve: resolve,
    refresh: function () { clearCache(); resolve(); },
    clearCache: clearCache,
    EVENT_NAME: EVENT_NAME
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolve);
  } else {
    resolve();
  }
})();
